import { Fragment, useContext, useState } from "react";
import { ThemeContext } from "../ThemeContext/ThemeContext";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  getSortedRowModel,
  SortingState,
  getExpandedRowModel,
  Row,
} from "@tanstack/react-table";
import { Form, Pagination, Table } from "react-bootstrap";
import { 
  faArrowUp, 
  faArrowDown,
  faAngleRight,
  faAngleDown,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faAngleDoubleLeft,
  faAngleDoubleRight
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classes from "./CustomTable.module.css";

const CustomTable = ({
  data,
  columns,
  expandable,
  renderSubComponent,
  title,
}: {
  data: any[] | null | undefined;
  columns: ColumnDef<any>[];
  expandable?: (row: Row<any>) => boolean;
  renderSubComponent?: (props: { row: Row<any> }) => React.ReactElement;
  title?: string;
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const { isDarkMode } = useContext(ThemeContext);

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: {
      sorting,
      globalFilter,
    },
    getRowCanExpand: expandable,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    debugTable: false,
  });

  const darkModeClasses = isDarkMode 
    ? classes.darkCard 
    : classes.lightCard;

  return (
    <div className={`${classes.tableCard} ${darkModeClasses}`}>
      {/* Header with title and global search */}
      <div className={classes.tableHeader}>
        <h5 className={classes.tableTitle}>{title || "Datos"}</h5>
        <div className={classes.searchWrapper}>
          <FontAwesomeIcon icon={faSearch} className={classes.searchIcon} />
          <input
            value={globalFilter ?? ''}
            onChange={e => setGlobalFilter(e.target.value)}
            placeholder="Buscar en todos los campos..."
            className={classes.globalSearch}
          />
        </div>
      </div>

      <div className={classes.tableWrapper}>
        <Table hover responsive className={`${classes.customTable} ${isDarkMode ? 'table-dark' : ''}`}>
          {/* Headers */}
          <thead className={`${classes.tableHead} ${isDarkMode ? classes.darkHeader : classes.lightHeader}`}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={classes.headerRow}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`${classes.headerCell} ${header.id !== "expander" ? classes.columnWidth : ""}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div className={classes.headerContent}>
                        <div className={classes.headerTitleWrapper}>
                          <button
                            type="button"
                            className={classes.sortButton}
                            onClick={header.column.getToggleSortingHandler()}
                            disabled={!header.column.getCanSort()}
                          >
                            <span className={classes.headerTitle}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            <span className={classes.sortIcon}>
                              {{
                                asc: <FontAwesomeIcon icon={faArrowUp} />,
                                desc: <FontAwesomeIcon icon={faArrowDown} />,
                              }[header.column.getIsSorted() as string] ?? null}
                            </span>
                          </button>
                        </div>
                        {header.column.getCanFilter() && (
                          <div className={classes.columnFilterWrapper}>
                            <FontAwesomeIcon icon={faSearch} className={classes.columnFilterIcon} />
                            <Form.Control
                              type="text"
                              value={(header.column.getFilterValue() ?? "") as string}
                              onChange={(e) => header.column.setFilterValue(e.target.value)}
                              placeholder={`Filtrar...`}
                              className={classes.columnFilter}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          
          {/* Body */}
          <tbody className={classes.tableBody}>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr 
                  className={`
                    ${classes.tableRow} 
                    ${row.getIsExpanded() ? classes.expandedRow : ''} 
                    ${isDarkMode ? classes.darkRow : classes.lightRow}
                  `}
                  data-theme={isDarkMode ? 'dark' : 'light'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={`${row.id} ${cell.id}`} className={classes.tableCell}>
                      {cell.column.id === 'expander' && expandable && expandable(row) ? (
                        <button 
                          onClick={() => row.toggleExpanded()}
                          className={classes.expandButton}
                        >
                          <FontAwesomeIcon 
                            icon={row.getIsExpanded() ? faAngleDown : faAngleRight} 
                          />
                        </button>
                      ) : 
                      flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
                {row.getIsExpanded() && (
                  <tr className={classes.expandedContent}>
                    <td colSpan={row.getVisibleCells().length} className={classes.expandedCell}>
                      <div className={classes.subComponentWrapper}>
                        {renderSubComponent ? renderSubComponent({ row }) : ""}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr className={classes.emptyRow}>
                <td colSpan={columns.length} className={classes.emptyCell}>
                  No se encontraron resultados
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination section */}
      <div className={classes.paginationWrapper}>
        <div className={classes.paginationInfo}>
          <span>
            Página {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount() || 1}
          </span>
        </div>
        
        <Pagination className={classes.paginationControls}>
          <Pagination.Item
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            className={classes.paginationItem}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </Pagination.Item>
          <Pagination.Item
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className={classes.paginationItem}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Pagination.Item>
          
          {generatePaginationItems()}
          
          <Pagination.Item
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className={classes.paginationItem}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Pagination.Item>
          <Pagination.Item
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            className={classes.paginationItem}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </Pagination.Item>
        </Pagination>
        
        <div className={classes.pageSizeSelector}>
          <span>Mostrar</span>
          <Form.Select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className={classes.pageSizeSelect}
          >
            {[10, 25, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </Form.Select>
          <span>registros</span>
        </div>
      </div>
    </div>
  );
  
  // Helper function to generate pagination numbers
  function generatePaginationItems() {
    const currentPage = table.getState().pagination.pageIndex;
    const totalPages = table.getPageCount();
    
    // Logic for showing a limited number of page numbers
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);
    
    // Adjust if at the start or end
    if (currentPage <= 1) {
      endPage = Math.min(3, totalPages - 1);
    } else if (currentPage >= totalPages - 2) {
      startPage = Math.max(0, totalPages - 4);
    }
    
    const items = [];
    
    // Add ellipsis at the beginning if needed
    if (startPage > 0) {
      items.push(
        <Pagination.Ellipsis key="ellipsis-start" disabled className={classes.paginationEllipsis} />
      );
    }
    
    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => table.setPageIndex(i)}
          className={`${classes.paginationItem} ${i === currentPage ? classes.activePage : ''}`}
        >
          {i + 1}
        </Pagination.Item>
      );
    }
    
    // Add ellipsis at the end if needed
    if (endPage < totalPages - 1) {
      items.push(
        <Pagination.Ellipsis key="ellipsis-end" disabled className={classes.paginationEllipsis} />
      );
    }
    
    return items;
  }
};

export default CustomTable;